/**
 * HttpClient - Pure HTTP operations with no business logic
 * Handles fetch requests with consistent error handling
 */
export class HttpClient {
  /**
   * Make an HTTP request
   * @param {string} url - Full URL to request
   * @param {Object} options - Fetch options; pass `signal` from an AbortController to cancel
   * @returns {Promise<Response>} The response object
   */
  async request(url, options = {}) {
    const selectedClinicId = sessionStorage.getItem('selectedClinicId');

    const fetchOptions = {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(selectedClinicId ? { 'X-Target-Clinic-Id': selectedClinicId } : {}),
        ...options.headers
      }
    };

    console.log(`[HttpClient] ${options.method || 'GET'} ${url}`);
    
    try {
      const response = await fetch(url, fetchOptions);
      console.log(`[HttpClient] ← Response: ${response.status} ${response.statusText}`);
      return response;
    } catch (error) {
      // A deliberate cancellation is not a failure; let callers recognise and ignore it.
      if (isAbortError(error)) {
        throw error;
      }
      // Network errors, CORS issues, etc.
      console.error(`[HttpClient] ✖ Network error:`, error);
      throw new HttpError('Network request failed', 0, error);
    }
  }

  /**
   * GET request
   */
  async get(url, options = {}) {
    return this.request(url, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  async post(url, body = null, options = {}) {
    return this.request(url, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined
    });
  }

  /**
   * Parse JSON response safely
   */
  async parseJson(response) {
    try {
      return await response.json();
    } catch (error) {
      console.warn(`[HttpClient] encountered error parsing body: ${response.body}`);
      return null;
    }
  }

  /**
   * Parse blob response
   */
  async parseBlob(response) {
    try {
      return await response.blob();
    } catch (error) {
      throw new HttpError('Failed to parse blob response', response.status, error);
    }
  }

  /**
   * Check if response is ok, throw error if not
   */
  checkResponse(response) {
    if (response.ok) {
      return response;
    }

    if (response.status === 401 || response.status === 403) {
      throw new AuthError('Authentication required', response.status);
    }

    throw new HttpError(`Request failed with status ${response.status}`, response.status);
  }
}

/**
 * True when a request was cancelled via AbortController rather than having failed.
 */
export const isAbortError = (error) =>
  error?.name === 'AbortError' || error?.code === 20;

/**
 * Custom error classes for better error handling
 */
export class HttpError extends Error {
  constructor(message, status, originalError = null) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.originalError = originalError;
  }
}

export class AuthError extends HttpError {
  constructor(message, status) {
    super(message, status);
    this.name = 'AuthError';
  }
}

